import React from 'react';
import { isInProgressStatus } from '../utils/realtimeStreamEligibility';
import './ProcessingProgressBar.css';

export interface ProcessingProgressBarProps {
  status: string;
  phase?: string;
  completed?: number;
  total?: number;
  failed?: number;
  label?: string;
  unit?: string;
  lastWorkerActivityAt?: string;
}

const PHASE_LABELS: Record<string, string> = {
  queued: 'Queued', pending: 'Queued', preparing: 'Preparing media',
  extracting: 'Analyzing media', preparing_segments: 'Preparing segments',
  indexing: 'Indexing', synthesizing: 'Summarizing',
  uploading: 'Uploading', receiving: 'Uploading', importing: 'Importing', scanning: 'Finding media',
  planning: 'Preparing media', rendering: 'Preparing media', publishing: 'Saving media',
};

/** A live activity indicator with measured work counts; it never predicts completion. */
const ProcessingProgressBar: React.FC<ProcessingProgressBarProps> = ({
  status, phase, completed, total, failed = 0, label, unit = 'segments', lastWorkerActivityAt,
}) => {
  if (!isInProgressStatus(status)) return null;
  const hasTotal = Number.isFinite(total) && Number(total) > 0;
  const count = Number.isFinite(completed) ? Math.max(0, Number(completed)) : 0;
  const totalCount = hasTotal ? Number(total) : 0;
  const activity = label || PHASE_LABELS[phase || status] || 'Processing';
  const workerActivity = lastWorkerActivityAt ? new Date(lastWorkerActivityAt) : null;
  const hasWorkerActivity = workerActivity && Number.isFinite(workerActivity.getTime());
  return (
    <div className="processing-progress" role="status" aria-live="polite">
      <div className="processing-progress-label">
        <span>{activity}</span>
        {hasTotal && <span>{count}/{totalCount} {unit}{failed > 0 ? ` · ${failed} failed` : ''}</span>}
      </div>
      {hasWorkerActivity && <span className="processing-worker-activity" title={`Last recorded worker activity: ${workerActivity.toLocaleString()}`}>
        Worker activity <time dateTime={lastWorkerActivityAt}>{workerActivity.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</time>
      </span>}
      <div className={`processing-progress-track${hasTotal ? '' : ' indeterminate'}`}
        role="progressbar" aria-label={activity}
        aria-valuemin={hasTotal ? 0 : undefined} aria-valuemax={hasTotal ? totalCount : undefined}
        aria-valuenow={hasTotal ? Math.min(count, totalCount) : undefined}>
        <span style={hasTotal ? { width: `${Math.min(count / totalCount, 1) * 100}%` } : undefined} />
      </div>
    </div>
  );
};

export default ProcessingProgressBar;
